import { useRef } from 'react'

import { useFetchFileApi } from 'api/files/queryFetchers/useFetchFileApi'
import { is404Error } from 'api/utils'
import { useStableCallback } from 'hooks/useStableCallback'
import { FileItemStatus } from 'types/files/files'

const CHECK_FILE_UPLOADED_INTERVAL = 5000
const ATTEMPT_LIMIT = 60
export const FILE_IS_INFECTED_ERROR = 'FILE_IS_INFECTED_ERROR'
export const FILE_IS_NOT_UPLOADED_ERROR = 'FILE_IS_NOT_UPLOADED_ERROR'
export const MAX_ATTEMPT_LIMIT = 'MAX_ATTEMPT_LIMIT'
export const FILE_DOES_NOT_EXIST_ERROR = 'FILE_DOES_NOT_EXIST_ERROR'
export const MAX_FILE_NAME_LENGTH = 128

export const useCheckFileUploaded = () => {
  const handleFetchFile = useFetchFileApi({ staleTime: 0 })

  const timeoutId = useRef<ReturnType<typeof setTimeout>>()

  const check = async (fileId: string, attempt = 0) => {
    try {
      const { data: file } = await handleFetchFile({ id: fileId })
      if (file.status === FileItemStatus.ACTIVE && attempt <= ATTEMPT_LIMIT) {
        return file
      } else if (file.status === FileItemStatus.INFECTED) {
        throw new Error(FILE_IS_INFECTED_ERROR)
      } else if (attempt > ATTEMPT_LIMIT) {
        throw new Error(MAX_ATTEMPT_LIMIT)
      } else {
        if (attempt < ATTEMPT_LIMIT) {
          return new Promise(resolve => {
            timeoutId.current = setTimeout(() => resolve(check(fileId, attempt + 1)), CHECK_FILE_UPLOADED_INTERVAL)
          })
        } else {
          throw new Error(MAX_ATTEMPT_LIMIT)
        }
      }
    } catch (e) {
      if (is404Error(e)) {
        throw new Error(FILE_DOES_NOT_EXIST_ERROR)
      } else {
        throw e
      }
    }
  }

  const checkFileUploaded = useStableCallback(async (fileId: string) => {
    await new Promise((resolve, reject) => check(fileId).then(resolve).catch(reject))
  })

  const cancelCheckFileUploaded = useStableCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
  })

  return {
    checkFileUploaded,
    cancelCheckFileUploaded,
  }
}
